import React, { useEffect, useRef, useState } from 'react';
import { aboutContent, siteInfo } from '../data/mock';
import { Award } from 'lucide-react';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="bg-[#0a0908] py-16 md:py-24 lg:py-32 px-4 md:px-6 overflow-hidden"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Title Section */}
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-[#8B7355] tracking-[0.2em] md:tracking-[0.4em] text-xs md:text-sm uppercase">
            {siteInfo.location} {siteInfo.tagline}
          </span>
          <h2 className="text-[#D4A853] text-2xl md:text-4xl lg:text-5xl font-serif mt-4 mb-2 break-words">
            {siteInfo.name}
          </h2>
          <p className="text-[#6B7B4B] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase mt-2">
            {siteInfo.subtitle}
          </p>
        </div>

        {/* Divider */}
        <div
          className={`flex items-center justify-center mb-12 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
          <div className="mx-4 w-2 h-2 rotate-45 border border-[#D4A853]" />
          <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
        </div>

        {/* Intro */}
        <div
          className={`text-center mb-12 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-[#8B0000] text-xl md:text-2xl tracking-widest uppercase mb-6">
            {aboutContent.title}
          </h3>
          <p className="text-[#A69080] text-base md:text-lg leading-relaxed">
            {aboutContent.intro}
          </p>
        </div>

        {/* Credentials */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-12 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {aboutContent.credentials.map((credential, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-4 border border-[#8B7355]/20 hover:border-[#D4A853]/50 transition-colors"
            >
              <Award className="w-5 h-5 text-[#D4A853] flex-shrink-0" />
              <span className="text-[#A69080] text-sm tracking-wide">{credential}</span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div
          className={`flex items-center justify-center mb-12 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
          <div className="mx-4 w-2 h-2 rotate-45 border border-[#6B7B4B]" />
          <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
        </div>

        {/* Philosophy */}
        <div
          className={`transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-[#A69080] text-base md:text-lg leading-relaxed space-y-6">
            {aboutContent.philosophy.split('\n').filter(p => p.trim()).map((paragraph, index) => (
              <p key={index} className="text-center">
                {paragraph.trim()}
              </p>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div
          className={`flex items-center justify-center my-12 transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent to-[#8B7355]/30" />
          <div className="mx-4 w-2 h-2 rotate-45 border border-[#D4A853]" />
          <div className="flex-1 h-[1px] bg-gradient-to-l from-transparent to-[#8B7355]/30" />
        </div>

        {/* CTA Buttons */}
        <div
          className={`flex flex-wrap justify-center gap-4 md:gap-6 transition-all duration-1000 delay-800 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-3 border border-[#D4A853] text-[#D4A853] text-sm tracking-[0.2em] uppercase hover:bg-[#D4A853] hover:text-[#0a0908] transition-all duration-300"
          >
            Apply to Serve
          </button>
          <button
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-3 border border-[#6B7B4B] text-[#6B7B4B] text-sm tracking-[0.2em] uppercase hover:bg-[#6B7B4B] hover:text-[#0a0908] transition-all duration-300"
          >
            View Services
          </button>
          <button
            onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-3 border border-[#8B0000] text-[#8B0000] text-sm tracking-[0.2em] uppercase hover:bg-[#8B0000] hover:text-white transition-all duration-300"
          >
            Gallery
          </button>
        </div>
      </div>
    </section>
  );
};

export default About;
